import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Briefcase, ArrowUpRight, AlertTriangle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const STAGE_STYLES = {
  "Not Started": "bg-slate-100 text-slate-600",
  "In Progress": "bg-blue-100 text-blue-800",
  "Needs Attention": "bg-amber-100 text-amber-800",
  "Complete": "bg-emerald-100 text-emerald-800",
  "Pending": "bg-slate-100 text-slate-500",
  "Assessment Readiness": "bg-indigo-100 text-indigo-800",
};

export default function EngagementList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/engagements/list")
      .then((r) => setRows(r.data.engagements || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) =>
    !q || (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.complyx_lead || "").toLowerCase().includes(q.toLowerCase())
  );

  const totalBlockers = rows.reduce((s, r) => s + (r.open_blockers || 0), 0);

  return (
    <div data-testid="engagement-list-page">
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-gold" /> Customer Engagements
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">
          A read-only operational cockpit summarizing each active customer's CMMC readiness across reconciliation, evidence, documentation and SSP — all derived from existing Command Center data.
        </p>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="engagement-summary-cards">
          <div className="rounded-md border border-border bg-card p-4">
            <div className="text-3xl font-display font-bold text-navy">{rows.length}</div>
            <div className="text-xs text-muted-foreground mt-1">Active Engagements</div>
          </div>
          <div className="rounded-md border border-border bg-card p-4">
            <div className="text-3xl font-display font-bold text-emerald-700">{rows.filter((r) => r.current_stage === "Assessment Readiness").length}</div>
            <div className="text-xs text-muted-foreground mt-1">Assessment Ready</div>
          </div>
          <div className="rounded-md border border-border bg-card p-4">
            <div className="text-3xl font-display font-bold text-amber-700">{rows.filter((r) => r.open_blockers > 0).length}</div>
            <div className="text-xs text-muted-foreground mt-1">With Open Blockers</div>
          </div>
          <div className="rounded-md border border-border bg-card p-4">
            <div className="text-3xl font-display font-bold text-navy">{totalBlockers}</div>
            <div className="text-xs text-muted-foreground mt-1">Total Blockers</div>
          </div>
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer or ComplyX lead…" className="pl-9" data-testid="engagement-search" />
        </div>

        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left">
              {["Customer", "Level", "Current Stage", "Readiness", "Controls", "Evidence", "Docs", "SSP", "Blockers", "ComplyX Lead", "Updated", ""].map((h) => (
                <th key={h} className="px-3 py-2.5 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-muted-foreground text-sm">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-muted-foreground text-sm">No customer engagements found.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.customer_id} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/operations/engagements/${r.customer_id}`)} data-testid="engagement-row">
                  <td className="px-3 py-2.5 font-medium text-navy">{r.customer_name || "—"}</td>
                  <td className="px-3 py-2.5 text-xs">{r.cmmc_level || "—"}</td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STAGE_STYLES[r.stages?.[r.current_stage]] || STAGE_STYLES[r.current_stage] || "bg-slate-100"}`}>{r.current_stage}</span></td>
                  <td className="px-3 py-2.5 text-xs">{r.readiness}</td>
                  <td className="px-3 py-2.5 text-xs text-emerald-700">{r.controls?.implemented || 0}/{r.controls?.total || 0}</td>
                  <td className="px-3 py-2.5 text-xs">{r.evidence?.accepted || 0} ok · {r.evidence?.open || 0} open</td>
                  <td className="px-3 py-2.5 text-xs">{r.documentation?.approved || 0} appr</td>
                  <td className="px-3 py-2.5 text-xs">{r.ssp?.status || "None"}{r.ssp?.version ? ` v${r.ssp.version}` : ""}</td>
                  <td className="px-3 py-2.5 text-xs">{r.open_blockers > 0 ? <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />{r.open_blockers}</span> : <span className="text-slate-400">0</span>}</td>
                  <td className="px-3 py-2.5 text-xs">{r.complyx_lead || "—"}</td>
                  <td className="px-3 py-2.5 text-xs">{String(r.last_updated || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2.5"><ArrowUpRight className="h-4 w-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
